import { Injectable } from '@angular/core';
import { ToReferenceFunction } from '@apollo/client/cache/core/types/common';
import { Store } from '@ngrx/store';
import { tap } from 'rxjs/operators';

import produce from 'immer';

import {
  ENTITY_CONNECTIONS_CHANGED
} from 'app/_state/entity/view-entity/entity-connections/entity-connection.actions';
import { extractFieldNameAndArgs } from 'core/apollo-cache-invalidator';

import {
  CreateEntityConnectionGQL, CreateEntityConnectionInput, GetEntityConnections
} from 'generated/graphql';

@Injectable({ providedIn: 'root' })
export class CreateEntityConnectionService {

  constructor(
    private _createEntityConnectionGQL: CreateEntityConnectionGQL,
    private _store: Store
  ) { }

  /**
   * Deletes an entity connection and performs query / cache
   * updates
   */
  createEntityConnection(
    input: CreateEntityConnectionInput
  ) {

    const produceNextState = (
      data: GetEntityConnections.GetEntityConnections,
      entityConnection: any,
      options: { toReference: ToReferenceFunction }
    ) => {
      const nextState = produce(data, draftState => {
        const { toReference } = options;
        const nodes = data.nodes.map(n => toReference(n).__ref);
        const hasNode = nodes.includes(toReference(entityConnection).__ref);
        if (hasNode) {
          return;
        }

        draftState.nodes = [
          entityConnection,
          ...data.nodes
        ];
        draftState.totalCount = data.totalCount + 1;
      });

      return nextState;
    };

    return this._createEntityConnectionGQL.mutate({
      input
    }, {
      update: (cache, result) => {
        const created = result.data.createEntityConnection.entityConnection;
        cache.modify({
          id: 'ROOT_QUERY',
          fields: {
            getEntityConnections(value: GetEntityConnections.GetEntityConnections, details) {
              const { args } = extractFieldNameAndArgs(details.storeFieldName);
              if (args.campaignId !== input.campaignId) {
                return value;
              }

              if ([created.entity1.id, created.entity2.id].includes(args.entityId)
                && args.entityConnectionTypeId === created.entityConnectionTypeId) {
                return produceNextState(value, created, details);
              }

              return value;
            }
          }
        });
      }
    }).pipe(
      tap((res) => {
        this._store.dispatch(
          ENTITY_CONNECTIONS_CHANGED({
            payload: {
              entityConnection: res.data.createEntityConnection.entityConnection
            }
          })
        );
      })
    );
  }
}
